import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errText, media } from "../../lib/api";
import { Badge, CustomerLink, OrderLink, Panel, RiskDot, ShopLink, Stat, money, shortDate, statusTone } from "./ui";

const GATEWAY_COLOURS = { stripe: "#635bff", tryb: "#b4884a" };

export default function Overview() {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = () => api.get("/admin/overview").then((r) => setD(r.data)).catch((e) => setError(errText(e)));
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  if (error) return <p className="text-danger text-sm" data-testid="overview-error">{error}</p>;
  if (!d) return <p className="text-sm text-muted" data-testid="overview-loading">Loading the floor…</p>;
  const k = d.kpis;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="kpi-deliveries-today" label="Deliveries today" value={k.deliveries_today} to="/admin/orders?scope=today"
              hint="Everything promised for today" />
        <Stat testid="kpi-revenue-today" label="Sold today" value={`${money(k.revenue_today_mad)} MAD`} hint={`${k.orders_today} orders · AOV ${money(k.aov_today_mad)}`} to="/admin/orders" />
        <Stat testid="kpi-at-risk" label="At risk" value={k.at_risk} tone={k.at_risk ? "bad" : undefined} to="/admin/orders?scope=at_risk"
              hint="Late, unaccepted or reported" />
        <Stat testid="kpi-awaiting" label="Awaiting acceptance" value={k.awaiting_acceptance ?? 0}
              tone={k.acceptance_overdue ? "bad" : k.awaiting_acceptance ? "warn" : undefined} to="/admin/orders?status=awaiting_acceptance"
              hint={k.acceptance_overdue ? `${k.acceptance_overdue} overdue (1 working hour passed)` : "Partner has the one-tap Accept link"} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="kpi-month-revenue" label="This month" value={`${money(k.revenue_month_mad)} MAD`} />
        <Stat testid="kpi-payouts" label="Payouts pending" value={`${money(k.pending_payouts_mad)} MAD`} hint={`${k.pending_payouts} statements`} to="/admin/finance" />
        <Stat testid="kpi-plans" label="Recurring plans" value={k.active_plans} hint={`${money(k.mrr_mad)} MAD a month`} to="/admin/subscriptions" />
        <Stat testid="kpi-queues" label="Waiting on you" value={k.pending_applications + k.pending_moderation + k.pending_reviews}
              hint={`${k.pending_applications} shops · ${k.pending_moderation} bouquets · ${k.pending_reviews} reviews`} to="/admin/applications" />
      </div>

      <Panel testid="deliveries-today-panel" title={`Deliveries today — ${d.deliveries_today.length}`}
             action={<Link className="btn-ghost text-xs" to="/admin/orders?scope=today">Open in orders</Link>}>
        {d.deliveries_today.length === 0 ? <p className="text-sm text-muted">Nothing due today.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="deliveries-today-table">
              <thead className="bg-plaster-deep">
                <tr>{["Order", "Status", "Window", "City", "Florist", "Recipient", "Total"].map((h) => <th key={h} className="text-start px-3 py-2 smallcaps">{h}</th>)}</tr>
              </thead>
              <tbody>
                {d.deliveries_today.map((o) => (
                  <tr key={o.id} className="border-t border-brass-light" data-testid={`delivery-today-${o.order_number}`}>
                    <td className="px-3 py-2"><OrderLink number={o.order_number} id={o.id} /></td>
                    <td className="px-3 py-2"><RiskDot level={o.risk} /> <Badge tone={statusTone(o.status)}>{o.status}</Badge></td>
                    <td className="px-3 py-2 text-xs">{o.delivery_window}</td>
                    <td className="px-3 py-2">{o.city}</td>
                    <td className="px-3 py-2"><ShopLink florist={o.florist} /></td>
                    <td className="px-3 py-2 text-xs">{o.recipient_name}</td>
                    <td className="px-3 py-2 tabular">{money(o.total_mad)} MAD</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="grid lg:grid-cols-3 gap-5">
        <Panel testid="trend-panel" title="Last 30 days" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={d.trend}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#b4884a" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#b4884a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e6ddd0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={4} />
              <YAxis tick={{ fontSize: 11 }} width={48} />
              <Tooltip formatter={(v, n) => [n === "revenue_mad" ? `${money(v)} MAD` : v, n === "revenue_mad" ? "Revenue" : "Orders"]} />
              <Area type="monotone" dataKey="revenue_mad" stroke="#14322a" fill="url(#rev)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="gateway-panel" title="Gateway split">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={d.gateway_split} dataKey="orders" nameKey="gateway" innerRadius={45} outerRadius={70}>
                {d.gateway_split.map((g) => <Cell key={g.gateway} fill={GATEWAY_COLOURS[g.gateway] || "#14322a"} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 text-sm">
            {d.gateway_split.map((g) => (
              <p key={g.gateway} className="flex justify-between" data-testid={`gateway-split-${g.gateway}`}>
                <Link to={`/admin/payments?gateway=${g.gateway}`} className="underline decoration-brass underline-offset-2">{g.gateway}</Link>
                <span className="tabular">{g.orders} · {money(g.revenue_mad)} MAD</span>
              </p>
            ))}
            <p className="text-xs text-muted">Taking payments through {d.gateways.active} right now.</p>
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Panel testid="city-panel" title="Cities">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={d.by_city}>
              <CartesianGrid stroke="#e6ddd0" vertical={false} />
              <XAxis dataKey="city" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} width={36} />
              <Tooltip />
              <Bar dataKey="orders" fill="#14322a" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="top-shops-panel" title="Top shops">
          {d.top_shops.map((s) => (
            <p key={s.florist_id} className="flex justify-between text-sm border-b border-brass-light py-1.5" data-testid={`top-shop-${s.florist_id}`}>
              <Link to={`/admin/florists/${s.florist_id}`} className="underline decoration-brass underline-offset-2">{s.shop_name}</Link>
              <span className="tabular">{s.delivered} · {money(s.revenue_mad)} MAD</span>
            </p>
          ))}
        </Panel>

        <Panel testid="top-products-panel" title="Best sellers">
          {d.top_products.map((p) => (
            <div key={p.id} className="flex items-center gap-3 text-sm border-b border-brass-light py-1.5">
              {p.image ? <img src={media(p.image)} alt="" className="w-9 h-11 object-cover" /> : null}
              <Link to={`/admin/catalogue?q=${encodeURIComponent(p.title)}`} className="underline decoration-brass underline-offset-2">{p.title}</Link>
              <span className="ms-auto tabular text-xs">{p.sold} sold · {money(p.price_mad)} MAD</span>
            </div>
          ))}
        </Panel>
      </div>

      {d.at_risk_orders.length ? (
        <Panel testid="at-risk-panel" title="Needs a human now" action={<Link className="btn-ghost text-xs" to="/admin/orders?scope=at_risk">See all</Link>}>
          {d.at_risk_orders.map((o) => (
            <p key={o.id} className="flex flex-wrap items-center gap-3 text-sm border-b border-brass-light py-1.5" data-testid={`at-risk-${o.order_number}`}>
              <OrderLink number={o.order_number} id={o.id} />
              <Badge tone="bad">{o.flags.join(", ") || o.status}</Badge>
              <span>{o.city} · {shortDate(o.delivery_date)}</span>
              <span className="ms-auto"><CustomerLink email={o.buyer_email} /></span>
            </p>
          ))}
        </Panel>
      ) : null}
    </div>
  );
}
